import { useQuery } from "@apollo/client";
import { useFlagsStatus, useUnleashClient, useUnleashContext } from "@unleash/proxy-client-react";
import Cookies from "js-cookie";
import React, { useEffect, useState } from "react";

import { GET_MEMBER_LAYOUT_INFO } from "../graphql/queries";
import { BREAKPOINT } from "../styles/variables";
import { MemberLayoutInfo } from "../types/__generated__/MemberLayoutInfo";
import { isLoggedIn } from "./auth";
import { PRESELECTED_WELCOME_PACK_ID_COOKIE } from "./constants";
import { logError } from "./logging";
import { takePrerenderReadyOwnership } from "./prerenderReadyOwnership";
import { getCountry } from "./urls.helper";

// Window-width cache shared across all useWindowWidthSize subscribers.
//
// Why: window.innerWidth is a layout-dependent property — reading it after a
// style invalidation (e.g. font swap) forces a synchronous layout flush. With
// many components calling useWindowWidthSize / useDeviceSize during a single
// React render, the cumulative cost is significant (~80 ms forced reflow on
// throttled mobile during the font-load window — see home-perf-followups.md).
//
// One module-level read at init + one read per actual resize event, broadcast
// to all subscribers, replaces N reads × M components on every commit.
let cachedWindowWidth = typeof window === "undefined" ? 0 : window.innerWidth;
const widthSubscribers = new Set<(w: number) => void>();
if (typeof window !== "undefined") {
  window.addEventListener(
    "resize",
    () => {
      cachedWindowWidth = window.innerWidth;
      widthSubscribers.forEach((cb) => cb(cachedWindowWidth));
    },
    { passive: true }
  );
}

/**
 * React hook that returns the current window innerWidth, kept in sync with
 * resize events. Reads layout once at module init and once per resize — not
 * once per component instance per render.
 */
const useWindowWidthSize = () => {
  const [windowWidth, setWindowWidth] = useState<number>(() => cachedWindowWidth);

  useEffect(() => {
    widthSubscribers.add(setWindowWidth);
    // Re-sync in case a resize fired between module init and effect mount.
    setWindowWidth(cachedWindowWidth);
    return () => {
      widthSubscribers.delete(setWindowWidth);
    };
  }, []);

  return windowWidth;
};

// Cached MediaQueryList instances so multiple callers share one MQL per query.
const mqlCache = new Map<string, MediaQueryList>();
const getMql = (query: string): MediaQueryList | null => {
  if (typeof window === "undefined" || !window.matchMedia) return null;
  if (!mqlCache.has(query)) mqlCache.set(query, window.matchMedia(query));
  return mqlCache.get(query) ?? null;
};

/**
 * Subscribe to a CSS media query. Unlike reading window.innerWidth, evaluating
 * a MediaQueryList does not trigger forced layout — the browser maintains the
 * match state internally and dispatches `change` events when crossings happen.
 */
const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState<boolean>(() => getMql(query)?.matches ?? false);

  useEffect(() => {
    const mql = getMql(query);
    if (!mql) return undefined;
    const handler = (event: MediaQueryListEvent) => setMatches(event.matches);
    mql.addEventListener("change", handler);
    // Re-sync in case the viewport changed between init and mount.
    setMatches(mql.matches);
    return () => mql.removeEventListener("change", handler);
  }, [query]);

  return matches;
};

// Breakpoint queries map 1:1 onto the legacy useDeviceSize semantics:
//   isExtraSmall   = width <= BREAKPOINT.XS  (575)
//   isSmall        = width <= BREAKPOINT.SM  (767)  — note: includes XS
//   isMedium       = width in (SM, MD]       (768..991)
//   isSmallOrMedium= width <= BREAKPOINT.MD  (991)
const MEDIA_QUERIES = {
  isExtraSmall: `(max-width: ${BREAKPOINT.XS}px)`,
  isSmall: `(max-width: ${BREAKPOINT.SM}px)`,
  isMedium: `(min-width: ${BREAKPOINT.SM + 1}px) and (max-width: ${BREAKPOINT.MD}px)`,
  isSmallOrMedium: `(max-width: ${BREAKPOINT.MD}px)`,
};

/**
 * Returns an object with booleans for device size based on width breakpoints.
 *
 * Uses matchMedia internally (not window.innerWidth) so it does not trigger
 * forced layout when called during a React commit. Same shape as before — all
 * call sites work unchanged.
 */
const useDeviceSize = (): {
  isExtraSmall: boolean;
  isSmall: boolean;
  isMedium: boolean;
  isSmallOrMedium: boolean;
} => ({
  isExtraSmall: useMediaQuery(MEDIA_QUERIES.isExtraSmall),
  isSmall: useMediaQuery(MEDIA_QUERIES.isSmall),
  isMedium: useMediaQuery(MEDIA_QUERIES.isMedium),
  isSmallOrMedium: useMediaQuery(MEDIA_QUERIES.isSmallOrMedium),
});

/**
 * Use this hook in a page component to scroll to the element corresponding to the URL hash on page load.
 */
export default function useScrollToHash(deps: React.DependencyList | undefined = []) {
  useEffect(() => {
    const { hash } = window.location;
    if (hash) {
      const id = hash.replace("#", "");
      const element = document.getElementById(id);
      if (element) element.scrollIntoView({ block: "start", behavior: "smooth" });
    }
  }, deps);
}

/**
 * React hook that initializes the unleash client
 */
const useInitUnleashClient = (): { isInitialized: boolean } => {
  const country = getCountry();
  const updateContext = useUnleashContext();
  const { flagsReady } = useFlagsStatus();
  const unleashClient = useUnleashClient();
  const userIsLoggedIn = isLoggedIn();
  const [isInitialized, setIsInitialized] = useState(false);

  const { data, loading: memberLayoutInfoDataLoading } = useQuery<MemberLayoutInfo>(GET_MEMBER_LAYOUT_INFO, {
    skip: !userIsLoggedIn,
  });

  const { externalUniqueId, subscription, createdDate } = data?.me || {};

  useEffect(() => {
    async function updateUnleashContextAndStartClient() {
      const defaultProperties = {
        countryCode: country,
      };
      let userProperties = {};
      if (externalUniqueId) {
        userProperties = {
          memberCreatedTimestamp: Math.round(new Date(createdDate).getTime() / 1000),
          subscriptionStatus: subscription?.subscriptionStatus?.name,
          subscriptionMonth: subscription?.monthCount,
          subscriptionFrequency: subscription?.monthFrequency,
          loggedIn: true,
        };
      } else {
        userProperties = {
          loggedIn: false,
        };
      }

      // Wait for the new flags to pull in from the different context
      await updateContext({
        userId: externalUniqueId ?? undefined,
        properties: {
          ...defaultProperties,
          ...userProperties,
        },
      });

      await unleashClient.start();
      setIsInitialized(true);
    }

    if (memberLayoutInfoDataLoading) return;

    // flagsReady == true means user context has been updated and client already started
    if (flagsReady) {
      setIsInitialized(true);
      return;
    }

    updateUnleashContextAndStartClient().catch((error) => {
      logError(error);
      setIsInitialized(true);
    });
  }, [externalUniqueId, subscription, createdDate, flagsReady, memberLayoutInfoDataLoading]);

  return { isInitialized };
};

/**
 * Custom hook for handling 'welcome-pack' URL parameter in the URL and setting it in a cookie.
 */
const useWelcomePackCookie = () => {
  useEffect(() => {
    // Get the URL parameter value
    const urlParams = new URLSearchParams(window.location.search);
    const preselectedWelcomePackId = urlParams.get("welcome-pack");

    if (preselectedWelcomePackId) {
      // Set the value in a cookie with a a day expiration time
      Cookies.set(PRESELECTED_WELCOME_PACK_ID_COOKIE, preselectedWelcomePackId, { expires: 1 });
    }
  }, []);
};

/**
 * Signals to the prerender function that the page content is ready.
 *
 * Calling this hook claims ownership of the prerenderReady flag — Layout
 * will skip its own auto-flip while the page is mounted, so the prerender
 * waits for the page's own readiness signal instead of Layout's
 * GET_MEMBER_LAYOUT_INFO query.
 *
 * App.js sets window.prerenderReady = false at module load. The prerender
 * function (netlify/functions/prerender.mts) treats false as authoritative
 * and will wait until maxWaitTime unless a route flips it to true. Pages
 * with their own data fetches should call this hook so they can flip the
 * flag once their data lands rather than relying on Layout's signal.
 *
 * @param isReady - pass false while the page is still loading data;
 *                  defaults to true for pages that render immediately.
 */
const usePrerenderReady = (isReady = true) => {
  useEffect(() => takePrerenderReadyOwnership(), []);

  useEffect(() => {
    // eslint-disable-next-line no-underscore-dangle
    if (window.__PRERENDER__ && isReady) {
      window.prerenderReady = true;
    }
  }, [isReady]);
};

/**
 * Tracks the user's `prefers-reduced-motion` setting so callers can skip
 * auto-advancing animations when the OS-level accessibility flag is on.
 */
const usePrefersReducedMotion = (): boolean => {
  const [prefersReduced, setPrefersReduced] = useState<boolean>(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const listener = (event: MediaQueryListEvent) => setPrefersReduced(event.matches);
    mql.addEventListener("change", listener);
    return () => mql.removeEventListener("change", listener);
  }, []);

  return prefersReduced;
};

export {
  useDeviceSize,
  useInitUnleashClient,
  usePrefersReducedMotion,
  usePrerenderReady,
  useScrollToHash,
  useWelcomePackCookie,
  useWindowWidthSize,
};
